import { Box, Stack } from '@ace-clublabs/shared-component-library';
import {
  AnimatePresence,
  motion,
  type Transition,
  type Variants,
} from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType, useOutlet } from 'react-router-dom';

const variants: Variants = {
  initial: (direction: number) => ({
    y: direction > 0 ? '100vh' : '-100vh',
    opacity: 0,
  }),
  animate: {
    y: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    y: direction > 0 ? '-100vh' : '100vh',
    opacity: 0,
  }),
};

const transition: Transition = {
  y: { type: 'tween', duration: 0.35, ease: 'easeInOut' },
  opacity: { duration: 0.2 },
};

/**
 * Captures the outlet element on mount and freezes it so that
 * exit animations render the old page content, not the new one.
 */
const FrozenOutlet = () => {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return frozen;
};

const ScrollFlowLayout: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const directionRef = useRef(0);

  const navStateDirection = (location.state as { direction?: 'back' } | null)
    ?.direction;
  const isBackwards = navigationType === 'POP' || navStateDirection === 'back';
  directionRef.current = isBackwards ? -1 : 1;

  const pathname = location.pathname;
  useEffect(() => {
    if (pathname) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        overflow: 'hidden',
        backgroundColor: 'background.default',
        borderRadius: '1.25rem 1.25rem 0 0',
        borderTop: '1px solid',
        borderRight: '1px solid',
        borderLeft: '1px solid',
        borderBottom: 'none',
        borderColor: 'grey.400',
        boxShadow:
          '0 -8px 32px 0 rgba(26, 28, 25, 0.14), 0 -2px 8px 0 rgba(26, 28, 25, 0.08)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Animated page area — fills remaining height, never overflows */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <AnimatePresence
          mode='popLayout'
          initial={false}
          custom={directionRef.current}
        >
          <motion.div
            key={location.pathname}
            custom={directionRef.current}
            variants={variants}
            initial='initial'
            animate='animate'
            exit='exit'
            transition={transition}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <FrozenOutlet />
          </motion.div>
        </AnimatePresence>
      </Box>
    </Stack>
  );
};

export default ScrollFlowLayout;
